import { useEffect, useState, useCallback } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Gauge, Rocket, PiggyBank, Target, Settings2, TrendingUp, Users, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const presets = {
  this_week: () => { const n = new Date(); const d = new Date(n); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); const e = new Date(d); e.setDate(d.getDate() + 6); return { start: iso(d), end: iso(e) }; },
  this_month: () => { const n = new Date(); return { start: iso(new Date(n.getFullYear(), n.getMonth(), 1)), end: iso(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; },
};

const MODES = {
  profit: {
    label: "PROFIT MODE", icon: PiggyBank, cls: "bg-primary text-primary-foreground",
    tagline: "You're at or near capacity. Stop adding overhead — squeeze more margin from the work you already have.",
    actions: ["Raise prices on new quotes (and renewals)", "Pause or cut paid advertising", "Fire / re-price your lowest-margin accounts", "Bank the extra profit — don't add trucks yet"],
  },
  growth: {
    label: "GROWTH MODE", icon: Rocket, cls: "bg-blue-600 text-white",
    tagline: "You have open capacity to fill. Go win more work before adding cost.",
    actions: ["Turn on / increase advertising & lead gen", "Follow up on every open quote & lead", "Ask happy customers for referrals & reviews", "Only add a crew/truck once you're consistently full"],
  },
  balanced: {
    label: "DIALED IN", icon: Target, cls: "bg-amber-500 text-white",
    tagline: "Healthy utilization. Hold steady and protect your margin as you fill the last slots.",
    actions: ["Keep the pipeline warm but don't overbook", "Watch for creep past capacity → switch to Profit Mode", "Tighten routes to free up hidden hours"],
  },
};

function Bar({ label, actual, target, kind, amount }) {
  // kind: 'max' (good when <= target) or 'min' (good when >= target)
  const good = kind === "min" ? actual >= target : actual <= target;
  const width = Math.min(100, actual);
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium flex items-center gap-1.5">{label}{good ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}</span>
        <span className="tabular-nums text-muted-foreground">{money(amount)} · <span className={good ? "text-primary font-semibold" : "text-amber-600 font-semibold"}>{actual}%</span> <span className="text-xs">(target {kind === "min" ? "≥" : "≤"}{target}%)</span></span>
      </div>
      <div className="relative h-3 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all ${good ? "bg-primary" : "bg-amber-500"}`} style={{ width: `${width}%` }} />
        <div className="absolute top-0 h-full w-0.5 bg-foreground/50" style={{ left: `${Math.min(100, target)}%` }} title={`Target ${target}%`} />
      </div>
    </div>
  );
}

export default function Capacity() {
  const { isAdmin } = useAuth();
  const [range, setRange] = useState(presets.this_week());
  const [data, setData] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState({ workers: 0, hours_per_worker: 32, growth_below: 70, profit_above: 85 });

  const load = useCallback(() => {
    api.get("/reports/capacity", { params: { start: range.start, end: range.end } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/capacity").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/settings/capacity", { ...cfg, workers: Number(cfg.workers) || 0, hours_per_worker: Number(cfg.hours_per_worker) || 32, growth_below: Number(cfg.growth_below), profit_above: Number(cfg.profit_above) });
      toast.success("Capacity settings saved");
      setCfgOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const cap = data.capacity;
  const f = data.financials;
  const t = data.targets;
  const m = MODES[data.mode];
  const ModeIcon = m.icon;
  const util = cap.utilization;
  const utilColor = util >= data.thresholds.profit_above ? "text-primary" : util <= data.thresholds.growth_below ? "text-blue-600" : "text-amber-500";

  return (
    <div className="space-y-6" data-testid="capacity-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">Based on Mike Andes' capacity model — decide whether to chase <strong>growth</strong> or bank <strong>profit</strong>, and keep your 50/30/20 cost structure in check.</p>
        <div className="flex items-end gap-2">
          <div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-36" data-testid="cap-start" /></div>
          <div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-36" data-testid="cap-end" /></div>
          <Button size="sm" variant="outline" onClick={() => setRange(presets.this_week())} data-testid="cap-this-week">This Week</Button>
          <Button size="sm" variant="outline" onClick={() => setRange(presets.this_month())} data-testid="cap-this-month">This Month</Button>
          {isAdmin && <Button size="sm" variant="ghost" onClick={() => setCfgOpen(true)} data-testid="cap-settings-btn"><Settings2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      {/* Mode banner */}
      <Card className={`p-6 ${m.cls}`} data-testid="mode-banner">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex items-center gap-4 md:w-1/2">
            <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center shrink-0"><ModeIcon className="h-7 w-7" /></div>
            <div>
              <div className="overline opacity-80">Recommendation</div>
              <div className="font-display text-3xl font-black tracking-tight" data-testid="mode-label">{m.label}</div>
              <p className="text-sm opacity-90 mt-1">{m.tagline}</p>
            </div>
          </div>
          <div className="md:w-1/2">
            <div className="overline opacity-80 mb-1.5">What to do now</div>
            <ul className="space-y-1 text-sm">
              {m.actions.map((a, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 opacity-90" />{a}</li>)}
            </ul>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Capacity gauge */}
        <Card className="p-6 space-y-4">
          <h3 className="font-semibold flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" />Capacity Utilization</h3>
          <div className="flex items-end gap-2">
            <span className={`font-display text-5xl font-black tabular-nums ${utilColor}`} data-testid="utilization-value">{util}%</span>
            <span className="text-sm text-muted-foreground mb-1.5">of crew hours booked</span>
          </div>
          <div className="relative h-4 rounded-full overflow-hidden bg-gradient-to-r from-blue-200 via-amber-200 to-primary/30">
            <div className="absolute top-0 h-full w-1 bg-foreground" style={{ left: `${Math.min(100, util)}%` }} />
          </div>
          <div className="flex justify-between text-[0.65rem] text-muted-foreground">
            <span>Growth ≤{data.thresholds.growth_below}%</span><span>Balanced</span><span>Profit ≥{data.thresholds.profit_above}%</span>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 text-sm">
            <div className="rounded-md bg-muted p-3"><div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Clock className="h-3.5 w-3.5" />Booked / Available</div><div className="font-display text-xl font-bold mt-1">{cap.booked_hours} / {cap.capacity_hours} h</div></div>
            <div className="rounded-md bg-muted p-3"><div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Users className="h-3.5 w-3.5" />Crew × Hours/wk</div><div className="font-display text-xl font-bold mt-1">{cap.workers} × {cap.hours_per_worker}h</div></div>
          </div>
          <p className="text-xs text-muted-foreground">{cap.jobs_count} jobs in this period{cap.auto_workers ? " · crew count auto-detected from active field staff (set it in ⚙︎ for accuracy)" : ""}.</p>
        </Card>

        {/* 50/30/20 */}
        <Card className="p-6 space-y-4">
          <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />50 / 30 / 20 Cost Structure</h3>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Revenue (period)</span>
            <span className="font-display text-2xl font-black" data-testid="cap-revenue">{money(f.revenue)}</span>
          </div>
          <div className="space-y-3">
            <Bar label="Direct costs (labor + materials)" actual={f.direct_pct} target={t.direct} kind="max" amount={f.direct_cost} />
            <Bar label="Overhead" actual={f.overhead_pct} target={t.overhead} kind="max" amount={f.overhead_cost} />
            <Bar label="Net profit" actual={f.profit_pct} target={t.profit} kind="min" amount={f.profit} />
          </div>
          <div className={`rounded-md p-3 text-sm flex items-center gap-2 ${f.labor_pct > t.labor_max ? "bg-amber-50 text-amber-700" : "bg-primary/5 text-primary"}`}>
            {f.labor_pct > t.labor_max ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            Labor is <strong className="mx-1">{f.labor_pct}%</strong> of revenue {f.labor_pct > t.labor_max ? `— above the ${t.labor_max}% ceiling. Raise prices or tighten job times.` : `(healthy, under ${t.labor_max}%).`}
          </div>
          {f.revenue === 0 && <p className="text-xs text-muted-foreground">No revenue recorded for this period yet — add invoices or complete priced jobs to see your cost structure.</p>}
        </Card>
      </div>

      {/* Settings dialog */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Capacity Settings</DialogTitle>
            <DialogDescription>Define your true field capacity so the mode recommendation is accurate.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Field workers</Label><Input type="number" value={cfg.workers} onChange={(e) => setCfg({ ...cfg, workers: e.target.value })} data-testid="cfg-workers" /><p className="text-[0.65rem] text-muted-foreground">0 = auto-count active staff</p></div>
            <div className="space-y-1.5"><Label>Billable hrs / worker / week</Label><Input type="number" value={cfg.hours_per_worker} onChange={(e) => setCfg({ ...cfg, hours_per_worker: e.target.value })} data-testid="cfg-hpw" /><p className="text-[0.65rem] text-muted-foreground">Andes suggests ~30–35</p></div>
            <div className="space-y-1.5"><Label>Growth below (%)</Label><Input type="number" value={cfg.growth_below} onChange={(e) => setCfg({ ...cfg, growth_below: e.target.value })} data-testid="cfg-growth" /></div>
            <div className="space-y-1.5"><Label>Profit above (%)</Label><Input type="number" value={cfg.profit_above} onChange={(e) => setCfg({ ...cfg, profit_above: e.target.value })} data-testid="cfg-profit" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCfgOpen(false)}>Cancel</Button>
            <Button onClick={saveCfg} data-testid="save-capacity-cfg">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
